import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiKeys as apiKeysApi, sites as sitesApi, ApiError, type ApiKeyScope, type CreatedApiKey } from '../lib/api'
import { useMe } from '../lib/useMe'
import { AuthLayout, ErrorBox, NoticeBox } from '../components/AuthLayout'
import { Dialog } from '../components/Dialog'
import { ConfirmDialog } from '../components/ConfirmDialog'

// API tokens for external integrations (docs/plans/2026-09-03-api-token-system-and-quotas.md):
// create/revoke, read vs read-write scope, last-usage. Mirrors Sites.tsx's inline-confirm
// pattern for revoke, and the plaintext key is shown exactly once — same convention every
// token-issuing product uses, because there is nothing to show a second time (only the
// hash is stored server-side).
export function ApiKeys() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useMe()
  const list = useQuery({ queryKey: ['api-keys'], queryFn: apiKeysApi.list, enabled: me.isSuccess })
  const sitesList = useQuery({ queryKey: ['sites'], queryFn: sitesApi.list, enabled: me.isSuccess })

  const [name, setName] = useState('')
  const [scope, setScope] = useState<ApiKeyScope>('read')
  const [orgWide, setOrgWide] = useState(false)
  const [siteId, setSiteId] = useState<number | ''>('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [reveal, setReveal] = useState<CreatedApiKey | null>(null)
  const [copied, setCopied] = useState(false)

  async function onCreate(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!orgWide && siteId === '') {
      setError('Kies een site, of schakel "Org-breed" in.')
      return
    }
    setBusy(true)
    try {
      const created = await apiKeysApi.create(name, scope, orgWide ? undefined : Number(siteId))
      setName('')
      setScope('read')
      setOrgWide(false)
      setSiteId('')
      setReveal(created)
      await queryClient.invalidateQueries({ queryKey: ['api-keys'] })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de API-key niet aanmaken.')
    } finally {
      setBusy(false)
    }
  }

  async function onRevoke(id: number) {
    setError('')
    try {
      await apiKeysApi.revoke(id)
      await queryClient.invalidateQueries({ queryKey: ['api-keys'] })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de API-key niet intrekken.')
    }
  }

  async function onCopy(key: string) {
    await navigator.clipboard.writeText(key)
    setCopied(true)
  }

  if (me.isError) {
    navigate('/login')
    return null
  }

  return (
    <AuthLayout title="API-keys" wide>
      <ErrorBox>{error}</ErrorBox>

      <Dialog
        open={!!reveal}
        onOpenChange={(open) => {
          if (!open) {
            setReveal(null)
            setCopied(false)
          }
        }}
        title="API-key aangemaakt"
      >
        {reveal && (
          <>
            <NoticeBox>Bewaar deze key nu — je krijgt hem hierna niet meer te zien.</NoticeBox>
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-line bg-paper px-3 py-2.5">
              <code className="flex-1 overflow-x-auto whitespace-nowrap text-sm text-ink">{reveal.key}</code>
              <button
                type="button"
                onClick={() => onCopy(reveal.key)}
                className="shrink-0 rounded-lg border border-line px-2.5 py-1 text-xs text-ink-soft hover:text-ink"
              >
                {copied ? 'Gekopieerd ✓' : 'Kopiëren'}
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                setReveal(null)
                setCopied(false)
              }}
              className="w-full rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90"
            >
              Sluiten
            </button>
          </>
        )}
      </Dialog>

      <div className="mb-4 rounded-xl border border-line bg-card p-5">
        <div className="mb-2 text-sm text-ink-soft">Nieuwe API-key aanmaken</div>
        <form onSubmit={onCreate}>
          <label className="mb-4 block">
            <span className="mb-1.5 block text-sm text-ink-soft">Naam</span>
            <input
              type="text"
              required
              placeholder="bijv. Zapier integratie"
              className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label className="mb-2 flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={orgWide} onChange={(e) => setOrgWide(e.target.checked)} />
            Org-breed (alle sites in deze organisatie)
          </label>
          {orgWide ? (
            <p className="mb-4 text-xs text-ink-soft">
              Let op: deze key kan bij alle huidige en toekomstige sites in je organisatie.
            </p>
          ) : (
            <label className="mb-4 block">
              <span className="mb-1.5 block text-sm text-ink-soft">Site</span>
              <select
                className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Kies een site…</option>
                {sitesList.data?.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name} ({site.domain})
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="mb-4">
            <span className="mb-1.5 block text-sm text-ink-soft">Scope</span>
            <label className="mb-1.5 flex items-center gap-2 text-sm text-ink">
              <input type="radio" name="scope" checked={scope === 'read'} onChange={() => setScope('read')} />
              Alleen lezen — statistieken opvragen
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="radio" name="scope" checked={scope === 'readwrite'} onChange={() => setScope('readwrite')} />
              Lezen en schrijven — kan ook metingen starten
            </label>
            {scope === 'readwrite' && (
              <p className="mt-1.5 text-xs text-ink-soft">
                Een readwrite-key kan metingen starten en dus credits verbruiken.
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90 disabled:opacity-50"
          >
            Aanmaken
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-line bg-card p-5">
        <div className="mb-2 text-sm text-ink-soft">Jouw API-keys</div>
        {list.isLoading ? (
          <p className="text-ink-soft">Laden…</p>
        ) : !list.data || list.data.length === 0 ? (
          <p className="text-ink-soft">Nog geen API-keys aangemaakt.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                <th className="border-b border-line pb-2">Naam</th>
                <th className="border-b border-line pb-2">Key</th>
                <th className="border-b border-line pb-2">Scope</th>
                <th className="border-b border-line pb-2">Site</th>
                <th className="border-b border-line pb-2">Laatst gebruikt</th>
                <th className="border-b border-line pb-2">Aangemaakt</th>
                <th className="border-b border-line pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((key) => (
                <tr key={key.id} className={key.revoked_at ? 'opacity-50' : undefined}>
                  <td className="border-b border-line py-2 text-ink">
                    {key.name}
                    {key.created_via === 'admin' && (
                      <span
                        title="Deze key is aangemaakt door Taufinity support, niet door jou."
                        className="ml-2 rounded-full border border-amber px-2 py-0.5 text-xs text-amber"
                      >
                        via support
                      </span>
                    )}
                  </td>
                  <td className="border-b border-line py-2 font-mono text-ink-soft">{key.key_prefix}…</td>
                  <td className="border-b border-line py-2">
                    <span className="rounded-full border border-line px-2 py-0.5 text-xs text-ink-soft">
                      {key.scope === 'readwrite' ? 'Lezen/schrijven' : 'Alleen lezen'}
                    </span>
                  </td>
                  <td className="border-b border-line py-2 text-ink-soft">{key.site_name || 'Org-breed'}</td>
                  <td className="border-b border-line py-2 text-ink-soft">
                    {key.last_used_at ? new Date(key.last_used_at).toLocaleString('nl-NL') : 'Nooit gebruikt'}
                  </td>
                  <td className="border-b border-line py-2 text-ink-soft">{new Date(key.created_at).toLocaleString('nl-NL')}</td>
                  <td className="border-b border-line py-2 text-right">
                    {key.revoked_at ? (
                      <span className="text-ink-soft">Ingetrokken</span>
                    ) : (
                      <ConfirmDialog
                        trigger={
                          <button
                            type="button"
                            className="rounded-lg border border-line px-2.5 py-1 text-xs text-ink-soft hover:border-[#C1666B] hover:text-[#e08a90]"
                          >
                            Intrekken
                          </button>
                        }
                        title="API-key intrekken"
                        description={`Weet je zeker dat je "${key.name}" wilt intrekken? Dit kan niet ongedaan worden gemaakt.`}
                        confirmLabel="Intrekken"
                        destructive
                        onConfirm={() => onRevoke(key.id)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AuthLayout>
  )
}
